import { HydrationBoundary, QueryClient, dehydrate } from "@tanstack/react-query";
import {
  data,
  useLoaderData,
  type HeadersFunction,
  type ShouldRevalidateFunction,
} from "react-router";
import EtfScreener from "../UI/pages/EtfScreener";
import { etfScreenerKeys } from "../api/services/etf-screener.hooks";
import {
  DEFAULT_ETF_SORT,
  EMPTY_ETF_FILTERS,
  ETF_PAGE_SIZE,
  etfFiltersToQuery,
} from "../UI/pages/EtfScreener/etfFilters";
import { serverConfig } from "../server/config.server";
import { fetchEtfScreenerPage } from "../server/etfScreenerData.server";

// Title, description and canonical come from the root meta (routeSeo.ts).

/** Seconds a page rendered without fund rows may be cached. */
const DEGRADED_TTL_SECONDS = 60;

const cacheControl = (ttl: number) =>
  `public, max-age=0, s-maxage=${ttl}, stale-while-revalidate=${ttl}`;

/**
 * Server-renders the default view (largest funds first, total count) so the
 * fund list is in the HTML. Filtered URLs render the same shell and load
 * their rows in the browser.
 */
export async function loader() {
  const queryClient = new QueryClient();
  const defaultQuery = etfFiltersToQuery(EMPTY_ETF_FILTERS, DEFAULT_ETF_SORT, 1, ETF_PAGE_SIZE);

  let rendered = true;
  try {
    await queryClient.fetchQuery({
      queryKey: etfScreenerKeys.list(defaultQuery),
      queryFn: () => fetchEtfScreenerPage(defaultQuery),
    });
  } catch (error) {
    console.error("etf screener SSR:", (error as Error).message);
    queryClient.clear();
    rendered = false;
  }

  const ttl = rendered
    ? Math.min(serverConfig.screenerCacheTtlSeconds, serverConfig.pageCacheTtlSeconds)
    : DEGRADED_TTL_SECONDS;

  return data(
    { dehydrated: dehydrate(queryClient) },
    { headers: { "Cache-Control": cacheControl(ttl) } },
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

// Filters, sort and paging live in component state and the URL query;
// changing them must not re-run the loader.
export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}) => (currentUrl.pathname !== nextUrl.pathname ? defaultShouldRevalidate : false);

export default function EtfScreenerRoute() {
  const { dehydrated } = useLoaderData<typeof loader>();
  return (
    <HydrationBoundary state={dehydrated}>
      <EtfScreener />
    </HydrationBoundary>
  );
}
